"use client"
import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from "lexical"
import { DecoratorNode, $getNodeByKey } from "lexical"
// import type { JSX } from "react"
// import { X } from "lucide-react"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import CloseIcon from "../../../icons/iconClose.comp"

export interface YouTubePayload {
  videoId: string
  key?: NodeKey
}

export type SerializedYouTubeNode = Spread<
  {
    videoId: string
  },
  SerializedLexicalNode
>

function convertYouTubeElement(domNode: Node): null | DOMConversionOutput {
  if (domNode instanceof HTMLIFrameElement) {
    const src = domNode.getAttribute("src") || ""
    const videoId = extractVideoId(src)
    if (videoId) {
      const node = $createYouTubeNode({ videoId })
      return { node }
    }
  }
  return null
}

function extractVideoId(url: string): string | null {
  // Handle various YouTube URL formats
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /^([a-zA-Z0-9_-]{11})$/, // Direct video ID
  ]

  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match && match[1]) {
      return match[1]
    }
  }
  return null
}

function YouTubeComponent({
  videoId,
  nodeKey,
}: {
  videoId: string
  nodeKey: NodeKey
}): JSX.Element {
  const [editor] = useLexicalComposerContext()

  const handleDelete = () => {
    editor.update(() => {
      const node = $getNodeByKey(nodeKey)
      if (node) {
        node.remove()
      }
    })
  }

  return (
    <div className="relative border-2 inline-block group my-4 w-[280px] h-[250px] py-2">
      <CloseIcon
        className="w-4 h-4 -right-1 absolute -top-1 bg-red-400 text-white rounded-lg cursor-pointer"
        onClick={handleDelete}
      />
        <iframe
          src={`https://www.youtube.com/embed/${videoId}`}
          title="YouTube video"
          className="w-full h-full rounded-lg"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
    </div>
  )
}

export class YouTubeNode extends DecoratorNode<JSX.Element> {
  __videoId: string

  static getType(): string {
    return "youtube"
  }

  static clone(node: YouTubeNode): YouTubeNode {
    return new YouTubeNode(node.__videoId, node.__key)
  }

  static importJSON(serializedNode: SerializedYouTubeNode): YouTubeNode {
    const { videoId } = serializedNode
    const node = $createYouTubeNode({ videoId })
    return node
  }

  exportJSON(): SerializedYouTubeNode {
    return {
      videoId: this.getVideoId(),
      type: "youtube",
      version: 1,
    }
  }

  static importDOM(): DOMConversionMap | null {
    return {
      iframe: (node: Node) => ({
        conversion: convertYouTubeElement,
        priority: 0,
      }),
    }
  }

  constructor(videoId: string, key?: NodeKey) {
    super(key)
    this.__videoId = videoId
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement("iframe")
    element.setAttribute("src", `https://www.youtube.com/embed/${this.__videoId}`)
    element.setAttribute("title", "YouTube video")
    element.setAttribute("width", "560")
    element.setAttribute("height", "315")
    element.setAttribute(
      "allow",
      "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture",
    )
    element.setAttribute("allowFullScreen", "true")
    return { element }
  }

  createDOM(config: EditorConfig): HTMLElement {
    const div = document.createElement("div")
    const theme = config.theme
    const className = theme.youtube
    if (className !== undefined) {
      div.className = className
    }
    return div
  }

  updateDOM(): false {
    return false
  }

  getVideoId(): string {
    return this.__videoId
  }

  decorate(): JSX.Element {
    return <YouTubeComponent videoId={this.__videoId} nodeKey={this.getKey()} />
  }
}

export function $createYouTubeNode({ videoId, key }: YouTubePayload): YouTubeNode {
  return new YouTubeNode(videoId, key)
}

export function $isYouTubeNode(node: LexicalNode | null | undefined): node is YouTubeNode {
  return node instanceof YouTubeNode
}
